import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import { appointmentResponseSchema } from "../../../services/prePlanningService.types";
import { appointmentRequiredShipmentDetailSchema } from "../../../types/inbound/prePlanning/aptrShipments.type";
import { http } from "../../../utils/httpCommon";
import { safeParsePartial } from "../../../utils/zodSafeParse";
import { useApiUrls } from "../../useApiUrl";

const APPOINTMENTS_SEARCH_TERM_KEY = "apptSearchTerm";

const appointmentsQueryKey = (
  sicId: number,
  planDate: string,
  apptSearchTerm: string | null = ""
) => ["pre-planning", sicId, "appointments", planDate, apptSearchTerm];

export function useGetAppointmentBucketData(
  sicId: number,
  planDate: string,
  apptSearchTerm = ""
) {
  const hookUrls = useApiUrls();
  const { getAppointmentBucket } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useQuery({
    refetchOnMount: true,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: appointmentsQueryKey(sicId, planDate, apptSearchTerm),
    queryFn: async () => {
      const url = getAppointmentBucket(sicId, planDate, apptSearchTerm);
      const { data } = await http.get(url);
      return isSafeParseEnabled
        ? safeParsePartial(
            appointmentResponseSchema,
            data,
            "useGetAppointmentBucketData"
          )
        : appointmentResponseSchema.parse(data);
    },
  });
}

export function useInvalidateQueryAppointmentBucketData() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const invalidateQueryAppointmentBucketData = (
    sicId: number,
    planDate: string
  ) => {
    queryClient.invalidateQueries({
      queryKey: appointmentsQueryKey(
        sicId,
        planDate,
        searchParams.get(APPOINTMENTS_SEARCH_TERM_KEY) ?? ""
      ),
    });
  };

  return { invalidateQueryAppointmentBucketData };
}

export function useGetAppointmentRequiredShipmentDetail({
  shipmentId,
  enabled = true,
}: {
  shipmentId: number;
  enabled: boolean;
}) {
  const hookUrls = useApiUrls();
  const { getAppointmentRequiredShipmentDetail } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useQuery({
    refetchOnMount: true,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      "pre-planning",
      "appointments-required",
      "shipment-detail",
      shipmentId,
    ],
    queryFn: async () => {
      const { data } = await http.get(
        getAppointmentRequiredShipmentDetail(shipmentId)
      );
      return isSafeParseEnabled
        ? safeParsePartial(
            appointmentRequiredShipmentDetailSchema,
            data,
            "useGetAppointmentRequiredShipmentDetail"
          )
        : appointmentRequiredShipmentDetailSchema.parse(data);
    },
    enabled,
  });
}
